/**
 * Required custom text for products that use the Infinite Options block.
 *
 * The "Enter Custom Text" fields on the Custom Printed BadgeBottoms products are
 * injected client-side by the Infinite Options / Customizery app. Shopify loads
 * that app script from its `script_tag` loader, which is bound to `window.load`,
 * so on a slow connection the container sits empty long after the page looks
 * finished. Nothing stopped a customer adding a made-to-order product to the cart
 * with no text at all, and those orders arrive unusable.
 *
 * This blocks add-to-cart until Custom Line 1 has a value, and tells the customer
 * which state the page is actually in - options still loading, or options present
 * but blank.
 *
 * Opted in per product by `data-bb-require-custom-text="true"` on the product form,
 * set in snippets/buy-buttons.liquid when the section has an `infinite_options_custom`
 * block. No jQuery, no dependency on custom-bb.js.
 */
(function () {
  'use strict';

  var CONTAINER_ID = 'infiniteoptions-container';
  var HIDDEN_INPUT_ID = 'bb_option_custom_line_1';
  var FIELD_NAME = 'properties[Custom Line 1]';
  var FORM_SELECTOR = 'form[data-bb-require-custom-text="true"]';

  // The app can inject its fields at any point after window.load, and custom-bb.js
  // pre-fills them programmatically from ?line1= URL params - a programmatic value
  // set fires no input event, so poll briefly as a backstop alongside the observer.
  var POLL_INTERVAL = 500;
  var POLL_DURATION = 60000;

  var entries = [];

  function container() {
    return document.getElementById(CONTAINER_ID);
  }

  /** The app-injected text field, if the app has run yet. */
  function visibleField() {
    var node = container();
    return node ? node.querySelector('[name="' + FIELD_NAME + '"]') : null;
  }

  /** True once the app has injected any option control into the container. */
  function optionsReady() {
    var node = container();
    return !!(node && node.querySelector('input, select, textarea'));
  }

  function valueOf(element) {
    return element && element.value ? element.value.trim() : '';
  }

  /**
   * The hidden input inside the form is what actually reaches the cart -
   * custom-bb.js mirrors the visible field into it. It can also carry a
   * server-rendered default from the cbb.custom_bb_text_1 metafield, so a blank
   * visible field is still valid when the hidden one is populated.
   */
  function hasText(form) {
    if (valueOf(visibleField())) return true;
    var hidden = (form && form.querySelector('#' + HIDDEN_INPUT_ID)) || document.getElementById(HIDDEN_INPUT_ID);
    return !!valueOf(hidden);
  }

  /**
   * Renders the note from current state. `entry.attempted` is sticky: once the
   * customer has been blocked at least once we validate eagerly, so the message
   * stays escalated instead of being reset to a soft hint by the next poll tick.
   */
  function render(entry) {
    var note = entry.note;
    var button = entry.button;

    if (hasText(entry.form)) {
      if (button) {
        button.classList.remove('bb-atc-blocked');
        // Leave aria-disabled alone when the theme has disabled the button for
        // stock reasons - SPID.setAddToCartOutOfStock owns it in that case.
        if (!button.disabled && button.hasAttribute('aria-disabled')) button.removeAttribute('aria-disabled');
      }
      if (note) {
        if (!note.hidden) note.hidden = true;
        note.classList.remove('bb-required-note--error');
      }
      return;
    }

    if (button) {
      // Styling hangs off our own class: setAddToCartOutOfStock clears
      // aria-disabled on this same button, so it cannot be the visual hook.
      button.classList.add('bb-atc-blocked');
      if (!button.disabled && button.getAttribute('aria-disabled') !== 'true') button.setAttribute('aria-disabled', 'true');
    }
    if (!note) return;

    var ready = optionsReady();
    var message;

    if (!ready) {
      message = note.getAttribute('data-message-loading');
    } else if (entry.attempted) {
      message = note.getAttribute('data-message-error');
    } else {
      message = note.getAttribute('data-message-empty');
    }

    if (entry.attempted) {
      if (note.getAttribute('role') !== 'alert') note.setAttribute('role', 'alert');
    } else if (note.hasAttribute('role')) {
      note.removeAttribute('role');
    }

    if (ready && entry.attempted) {
      note.classList.add('bb-required-note--error');
    } else {
      note.classList.remove('bb-required-note--error');
    }

    // render() runs from a MutationObserver, from every input and change event
    // and from a 500ms poll, so an unconditional write here fed the page-wide
    // observers back into itself - about 40 wake-ups a second at idle. Every
    // write below is now conditional, which makes render() idempotent.
    var next = message || '';
    if (entry.text && entry.text.textContent !== next) entry.text.textContent = next;
    if (note.hidden) note.hidden = false;
  }

  function refresh() {
    entries.forEach(render);
  }

  function focusField() {
    var field = visibleField();
    if (!field) return;
    if (typeof field.scrollIntoView === 'function') {
      field.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
    field.focus({ preventScroll: true });
  }

  function entryFor(form) {
    for (var i = 0; i < entries.length; i++) {
      if (entries[i].form === form) return entries[i];
    }
    return null;
  }

  function collectForms() {
    var found = document.querySelectorAll(FORM_SELECTOR);

    Array.prototype.forEach.call(found, function (form) {
      if (entryFor(form)) return;

      var note = form.querySelector('[data-bb-required-note]');
      entries.push({
        form: form,
        note: note,
        text: note ? note.querySelector('.bb-required-note__text') : null,
        button: form.querySelector('.product-form__submit'),
        attempted: false
      });
    });

    return entries.length > 0;
  }

  /**
   * Capture on the document so this runs before product-form.js, which binds its
   * own submit listener directly on the form in its constructor. Stopping the
   * event here means its fetch to /cart/add never starts.
   */
  document.addEventListener(
    'submit',
    function (event) {
      var form = event.target;
      if (!form || form.getAttribute('data-bb-require-custom-text') !== 'true') return;
      // Fail open: no container means this product does not use the app after all,
      // so there is nothing to wait for and nothing to require. Never make a
      // product unbuyable because the Liquid opt-in was too broad.
      if (!container()) return;
      if (hasText(form)) return;

      event.preventDefault();
      event.stopImmediatePropagation();

      var entry = entryFor(form);
      if (!entry) return;

      entry.attempted = true;
      render(entry);
      if (optionsReady()) focusField();
    },
    true
  );

  document.addEventListener('input', refresh);
  document.addEventListener('change', refresh);
  window.addEventListener('load', refresh);

  function start() {
    var node = container();
    if (!node) return;
    if (!collectForms()) return;
    refresh();

    if (window.MutationObserver) {
      new MutationObserver(refresh).observe(node, { childList: true, subtree: true });
    }

    var elapsed = 0;
    var poll = setInterval(function () {
      elapsed += POLL_INTERVAL;
      if (elapsed >= POLL_DURATION) clearInterval(poll);
      refresh();
    }, POLL_INTERVAL);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
